import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { getSocialReport, useQuery } from "wasp/client/operations";
import { useLanguageContext } from "../../client/contexts/LanguageContext";
import useTranslation from "../../client/hooks/useTranslation";
import type { LifecycleAccountRef, LifecycleFundedRef, LifecycleOutcomes } from "../../social-reports/metrics/computeLifecycle";
import type { ReportMetrics } from "../../social-reports/metrics/types";
import { useLiteData } from "../data/LiteDataProvider";
import { PageBody, PageHeader } from "../shell/LiteLayout";
import { MetricGrid, type Metric } from "../ui/analytics";
import { Bar, Card, cardCls, Empty, Stat } from "../ui/ui";
import { hasPropFirmPart, reportMoney, roiText } from "./reports/reportFormat";
import { asReviewPeriod, periodLabel, utcCalendarDate } from "./reports/reportPeriod";

type Money = ReturnType<typeof reportMoney>;
const tone = (v: number) => (v > 0 ? "gain" : v < 0 ? "loss" : undefined);

/** Détail d'un bilan Lite : la partie trading, puis la partie prop firm. Chiffres seulement. */
export default function LiteReportDetailPage() {
  const tl = useTranslation().lite;
  const t = tl.reports;
  const { language } = useLanguageContext();
  const { fmt } = useLiteData();
  const { reportId } = useParams<{ reportId: string }>();
  const { data: report, isLoading, error } = useQuery(getSocialReport, { id: reportId ?? "" }, { enabled: !!reportId });

  const back = (
    <Link to="/reports" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
      <ArrowLeft size={14} />{t.detail.back}
    </Link>
  );

  if (isLoading) {
    return <><div className="pt-2 md:pt-4">{back}</div><PageBody><div className={cardCls}><Empty title={tl.common.loading} /></div></PageBody></>;
  }
  if (error || !report) {
    return <><div className="pt-2 md:pt-4">{back}</div><PageBody><div className={cardCls}><Empty title={t.detail.notFound} /></div></PageBody></>;
  }

  const m = report.metricsSnapshot as unknown as ReportMetrics;
  const money = reportMoney(fmt, m.currency ?? "USD");
  const period = asReviewPeriod(report.period);
  const title = period
    ? periodLabel(period, utcCalendarDate(report.periodStart), language)
    : `${fmt.dateShort(report.periodStart)} – ${fmt.dateShort(report.periodEnd)}`;
  const legacy = m.schemaVersion !== 2;

  return (
    <>
      <div className="pt-2 md:pt-4 -mb-2">{back}</div>
      <PageHeader
        title={title}
        subtitle={`${m.scopeLabel ?? t.list.scopeAll} · ${t.list.generatedOn(fmt.dateShort(report.createdAt))}`}
        filters={false}
      />
      <PageBody>
        {legacy && <p className="text-sm text-muted-foreground px-1">{t.detail.legacy}</p>}

        <SectionTitle title={t.detail.trading} subtitle={t.detail.tradingSubtitle} />
        <TradingPart m={m} money={money} />

        {hasPropFirmPart(m) && (<>
          <SectionTitle title={t.detail.propFirm} subtitle={t.detail.propFirmSubtitle} />
          <PropFirmPart m={m} money={money} />
        </>)}
      </PageBody>
    </>
  );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="pt-2 px-1">
      <h2 className="font-display text-lg font-bold">{title}</h2>
      <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
    </div>
  );
}

function TradingPart({ m, money }: { m: ReportMetrics; money: Money }) {
  const tl = useTranslation().lite;
  const t = tl.reports.detail;
  const { fmt } = useLiteData();
  if (!m.tradesCount) return <div className={cardCls}><Empty title={t.noTrades} /></div>;

  const d = m.dailyActivity;
  const metrics = ([
    { label: t.m.net, value: money(m.pnlNet), tone: tone(m.pnlNet), hint: tl.common.trades(m.tradesCount) },
    { label: t.m.winRate, value: fmt.pct(m.winRate, 1), hint: t.m.winRateHint(m.longStats?.trades ?? 0, m.shortStats?.trades ?? 0) },
    { label: t.m.pf, value: m.profitFactor === null ? "-" : fmt.num(m.profitFactor) },
    { label: t.m.avgTrade, value: money(m.avgPnlPerTrade ?? 0), tone: tone(m.avgPnlPerTrade ?? 0) },
    d && { label: t.m.daysTraded, value: String(d.daysTraded), hint: t.m.daysTradedHint(d.periodTradingDays) },
    d && { label: t.m.avgDay, value: money(d.avgPnlPerDay), tone: tone(d.avgPnlPerDay) },
    { label: t.m.grossProfit, value: money(m.grossProfit, { sign: false }), tone: "gain" as const },
    { label: t.m.grossLoss, value: money(-m.grossLoss), tone: "loss" as const },
    m.bestTrade && { label: t.m.bestTrade, value: money(m.bestTrade.pnl), tone: "gain" as const, hint: m.bestTrade.symbol },
    m.worstTrade && { label: t.m.worstTrade, value: money(m.worstTrade.pnl), tone: "loss" as const, hint: m.worstTrade.symbol },
    m.bestDay && { label: t.m.bestDay, value: money(m.bestDay.pnl), tone: "gain" as const, hint: fmt.dateShort(m.bestDay.date) },
    m.worstDay && { label: t.m.worstDay, value: money(m.worstDay.pnl), tone: "loss" as const, hint: fmt.dateShort(m.worstDay.date) },
    m.maxDrawdown !== null && m.maxDrawdown !== undefined && { label: t.m.drawdown, value: money(-Math.abs(m.maxDrawdown)), tone: "loss" as const },
    { label: t.m.streaks, value: `${m.longestWinStreak ?? 0} / ${m.longestLossStreak ?? 0}`, hint: t.m.streaksHint },
  ] as (Metric | null | undefined | false)[]).filter((x): x is Metric => !!x);

  const symbols = (m.bySymbol ?? []).slice().sort((a, b) => b.pnlNet - a.pnlNet);

  return (
    <>
      <MetricGrid metrics={metrics} />
      {symbols.length > 0 && (
        <Card title={t.symbols.title} pad={false}>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="text-left font-medium px-5 py-2">{t.symbols.symbol}</th>
                  <th className="text-right font-medium px-3 py-2">{t.symbols.trades}</th>
                  <th className="text-right font-medium px-3 py-2">{t.symbols.winRate}</th>
                  <th className="text-right font-medium px-5 py-2">{t.symbols.net}</th>
                </tr>
              </thead>
              <tbody>
                {symbols.map(s => (
                  <tr key={s.symbol} className="border-t border-border">
                    <td className="px-5 py-2.5 font-medium">{s.symbol}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums text-muted-foreground">{s.trades}</td>
                    <td className="px-3 py-2.5 text-right font-mono tabular-nums">{fmt.pct(s.winRate)}</td>
                    <td className={`px-5 py-2.5 text-right font-mono tabular-nums font-semibold ${s.pnlNet > 0 ? "text-pnlPositive" : s.pnlNet < 0 ? "text-pnlNegative" : ""}`}>{money(s.pnlNet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

function PropFirmPart({ m, money }: { m: ReportMetrics; money: Money }) {
  const t = useTranslation().lite.reports.detail;
  const { fmt } = useLiteData();
  const finance = m.propFirmFinance ?? null;
  const payouts = m.payoutsSummary ?? null;
  const life = m.lifecycle ?? null;
  const adv = m.challengeAdvancement ?? null;
  if (!finance && !payouts && !life && !adv) return <div className={cardCls}><Empty title={t.noPropFirm} /></div>;

  const accountLabel = (a: LifecycleAccountRef) => (a.propFirmName ? `${a.name} · ${a.propFirmName}` : a.name);
  const fundedLabel = (a: LifecycleFundedRef) => (a.unlinked ? `${accountLabel(a)} (${t.lifecycle.unlinked})` : accountLabel(a));
  const outcomes = life?.outcomes;
  const outcomeKeys: Array<keyof LifecycleOutcomes> = ["blown", "likelyBlown", "passed", "active"];
  const hasMoves = !!life && (life.added.count > 0 || life.blown.count > 0 || life.blown.lowConfidenceCount > 0 || life.funded.count > 0);

  return (
    <>
      {finance && (
        <Card title={t.finance.title}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
            <Stat label={t.finance.spent} value={money(-finance.spent, { sign: false })} tone={finance.spent ? "loss" : undefined} />
            <Stat label={t.finance.payouts} value={money(finance.payouts)} tone={finance.payouts ? "gain" : undefined} />
            <Stat label={t.finance.net} value={money(finance.net)} tone={tone(finance.net)} />
            <Stat label={t.finance.roi} value={roiText(finance.roi, fmt)} tone={finance.roi === null ? undefined : finance.roi >= 0 ? "gain" : "loss"} hint={finance.roi === null ? t.finance.roiNone : undefined} />
          </div>
        </Card>
      )}

      <div className="grid xl:grid-cols-2 gap-5">
        {payouts && (
          <Card title={t.payouts.title} subtitle={payouts.count ? t.payouts.count(payouts.count) : undefined}>
            {payouts.count === 0 ? (
              <p className="text-sm text-muted-foreground">{t.payouts.none}</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-x-6">
                  <Stat label={t.finance.payouts} value={money(payouts.total)} tone="gain" />
                  {payouts.best && <Stat label={t.payouts.best} value={money(payouts.best.amount)} hint={payouts.best.propFirmName ?? fmt.dateShort(payouts.best.date)} />}
                </div>
                {payouts.byPropFirm.length > 0 && (
                  <div className="space-y-2 text-sm pt-3 border-t border-border">
                    {payouts.byPropFirm.map((f, i) => (
                      <div key={`${f.propFirmName ?? ""}-${i}`} className="flex justify-between gap-3">
                        <span className="text-muted-foreground truncate">{f.propFirmName ?? t.payouts.noFirm} <span className="text-muted-foreground/60">· {f.count}</span></span>
                        <span className="font-mono tabular-nums text-pnlPositive">{money(f.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {life && (
          <Card title={t.lifecycle.title}>
            {!hasMoves ? (
              <p className="text-sm text-muted-foreground">{t.lifecycle.none}</p>
            ) : (
              <div className="grid grid-cols-3 gap-x-4">
                <MoveColumn label={t.lifecycle.added} count={life.added.count} names={life.added.accounts.map(accountLabel)} />
                <MoveColumn
                  label={t.lifecycle.blown}
                  count={life.blown.count}
                  names={life.blown.accounts.map(accountLabel)}
                  tone="loss"
                  note={life.blown.lowConfidenceCount > 0 ? t.lifecycle.lowConfidence(life.blown.lowConfidenceCount) : undefined}
                />
                <MoveColumn label={t.lifecycle.funded} count={life.funded.count} names={life.funded.accounts.map(fundedLabel)} tone="gain" />
                {outcomes && life.added.count > 0 && (
                  <div className="col-span-3 mt-4 pt-3 border-t border-border">
                    <div className="text-xs text-muted-foreground">{t.lifecycle.outcomesTitle(life.added.count)}</div>
                    <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm">
                      {outcomeKeys.filter((k) => outcomes[k] > 0).map((k) => (
                        <span key={k} className="text-muted-foreground">
                          {t.lifecycle.outcomes[k]} <span className="font-mono tabular-nums font-semibold text-foreground">{outcomes[k]}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}
      </div>

      {adv && (
        <Card title={t.advancement.title}>
          {adv.accounts.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t.advancement.none}</p>
          ) : (
            <div className="space-y-5">
              {adv.accounts.map((row, i) => {
                const sub = [row.propFirmName, row.tierLabel].filter(Boolean).join(" · ");
                return (
                  <div key={`${row.name}-${i}`} className="space-y-1.5">
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{row.name}</div>
                        {sub && <div className="text-xs text-muted-foreground truncate">{sub}</div>}
                      </div>
                      <div className="font-mono tabular-nums text-sm shrink-0">
                        {money(row.profit)}
                        {row.targetAmount !== null && <span className="text-muted-foreground"> / {money(row.targetAmount, { sign: false })}</span>}
                      </div>
                    </div>
                    <Bar value={row.ratio} tone={row.isComplete ? "gain" : "brand"} />
                    <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                      <span>{t.advancement.days} : <span className="font-mono">{row.daysTraded}{row.minTradingDays !== null && ` / ${row.minTradingDays}`}</span></span>
                      {row.isComplete && <span className="inline-flex items-center gap-1 text-pnlPositive"><CheckCircle2 size={12} />{t.advancement.complete}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}
    </>
  );
}

function MoveColumn({ label, count, names, tone, note }: { label: string; count: number; names: string[]; tone?: "gain" | "loss"; note?: string }) {
  return (
    <div className="min-w-0">
      <Stat label={label} value={String(count)} tone={count ? tone : undefined} />
      {names.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {names.map((n, i) => <li key={`${n}-${i}`} className="text-xs text-muted-foreground truncate">{n}</li>)}
        </ul>
      )}
      {note && <div className="text-xs text-muted-foreground/70 mt-1">{note}</div>}
    </div>
  );
}
